import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/lib/api";

export function SectionHead({ numero, libelle, titre, chapeau, id }) {
  return (
    <>
      <div className="sec-num" data-testid={`sec-num-${id}`}>{libelle}</div>
      <div>
        <Reveal><h2 className="t-h2" style={{ margin: 0, maxWidth: 900 }}>{titre}</h2></Reveal>
        {chapeau && <Reveal delay={0.1}><p className="t-chapo" style={{ marginTop: 22, maxWidth: 640 }}>
          {chapeau.split("\n").map((line, i) => (
            <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
          ))}
        </p></Reveal>}
      </div>
    </>
  );
}

export function Chapo({ data }) {
  if (!data) return null;
  return (
    <section className="wrap" style={{ paddingTop: 96, paddingBottom: 88 }} data-testid="chapo-section">
      <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-end">
        <Reveal>
          <p className="t-h2" style={{ margin: 0, maxWidth: 760 }}>
            {data.texte.split("\n").map((line, i) => (
              <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
            ))}
          </p>
          {data.sousTitre && (
            <p className="t-body" style={{ marginTop: 18, marginBottom: 0, maxWidth: 680, color: "var(--ink2)" }} data-testid="chapo-sous-titre">
              {data.sousTitre}
            </p>
          )}
        </Reveal>
        <Reveal delay={0.12}>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            <a href="/estimer/" className="btn btn-primary" data-testid="chapo-cta-estimer">{data.ctaPrimaire || "Estimer ma campagne"}</a>
            <a href="#realisations" className="btn" data-testid="chapo-cta-travail">{data.ctaSecondaire}</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Promesses({ items }) {
  if (!items) return null;
  return (
    <div className="wrap" data-testid="promesses-section">
      <div className="promesses">
        {items.map((p, i) => (
          <Reveal key={p.mot} delay={i * 0.08} className="promesse">
            <p className="mot" data-testid={`promesse-mot-${i}`}>{p.mot}</p>
            <p className="t-body" style={{ margin: 0, fontSize: 15 }}>{p.texte}</p>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

function ExpertiseCol({ col, testid }) {
  return (
    <div className="exp-col" data-testid={testid}>
      <Reveal>
        <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 18 }}>
          <h3 className="t-h3" style={{ margin: 0 }}>{col.titre}</h3>
          <span className="t-label" style={{ color: "var(--roi)", fontWeight: 700 }}>{col.accroche}</span>
        </div>
      </Reveal>
      <Reveal delay={0.08}><p className="t-body" style={{ marginTop: 0 }}>{col.texte}</p></Reveal>
      <div style={{ marginTop: 12 }}>
        {col.entrees.map((e) => (
          <Link to={e.url} key={e.nom} className="exp-row" data-testid={`exp-${e.nom.replace(/\s/g, "-").toLowerCase()}`}>
            <span className="nom">{e.nom}</span>
            <span className="det">{e.detail}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function Expertise({ data }) {
  if (!data) return null;
  return (
    <section id="expertise" className="sec wrap" data-testid="expertise-section">
      <SectionHead id="expertise" numero={data.numero} libelle={data.libelle} titre={data.titre} chapeau={data.chapeau} />
      <div className="exp-cols" style={{ marginTop: 56, gridColumn: "2" }}>
        <ExpertiseCol col={data.studio} testid="expertise-studio" />
        <ExpertiseCol col={data.regie} testid="expertise-regie" />
      </div>
    </section>
  );
}

function WorkCard({ p, i }) {
  const [active, setActive] = useState(false);
  return (
    <Reveal delay={(i % 2) * 0.08}>
      <Link
        to={`/realisations/${p.slug}/`}
        className="work"
        data-testid={`work-${p.slug}`}
        onMouseEnter={() => setActive(true)}
        onMouseLeave={() => setActive(false)}
      >
        <div className="work-frame">
          <img src={p.poster || `/posters/projet-${p.slug}.svg`} alt={`${p.titre} — ${p.categorie}, ${p.client}`} loading="lazy" width="1600" height="900" />
          {p.videoUrl && active && (
            <video
              className="work-preview"
              src={p.videoUrl}
              muted
              loop
              autoPlay
              playsInline
              preload="auto"
              aria-hidden="true"
              data-testid={`work-preview-${p.slug}`}
            />
          )}
          <div className="work-play" aria-hidden="true"><span><Play size={18} strokeWidth={1.5} /></span></div>
        </div>
        <div className="work-line">
          <span className="work-titre">{p.titre}</span>
          <span className="work-meta">{p.categorie} · {p.client} · {p.annee}</span>
        </div>
      </Link>
    </Reveal>
  );
}

export function RealisationsGrid({ projects, filtres }) {
  const [filtre, setFiltre] = useState(filtres?.[0] || "Tout");
  const list = filtre === "Tout" ? projects : projects.filter((p) => p.categorie === filtre);
  return (
    <div>
      {filtres && (
        <div className="works-filters" style={{ marginBottom: 44 }} role="tablist">
          {filtres.map((f) => (
            <button
              key={f}
              className={`works-filter ${filtre === f ? "active" : ""}`}
              onClick={() => setFiltre(f)}
              data-testid={`filtre-${f.replace(/\s/g, "-").toLowerCase()}`}
            >
              {f}
            </button>
          ))}
        </div>
      )}
      <div className="works-grid">
        {list.map((p, i) => (
          <WorkCard key={p.slug} p={p} i={i} />
        ))}
      </div>
    </div>
  );
}

export function Realisations({ sec, projects }) {
  if (!sec) return null;
  return (
    <section id="realisations" className="sec wrap" data-testid="realisations-section">
      <SectionHead id="realisations" numero={sec.numero} libelle={sec.libelle} titre={sec.titre} chapeau={sec.chapeau} />
      <div style={{ gridColumn: "2", marginTop: 56 }}>
        <RealisationsGrid projects={projects || []} filtres={sec.filtres} />
      </div>
    </section>
  );
}

export function BandeauImage() {
  return (
    <div className="bandeau-img" data-testid="bandeau-image">
      <img src="/posters/bandeau.svg" alt="Plateau SLIZ — régie et studio réunis dans la même maison" loading="lazy" width="1920" height="640" />
    </div>
  );
}

export function Regie({ data }) {
  if (!data) return null;
  return (
    <section id="regie" className="sec wrap" data-testid="regie-section">
      <SectionHead id="regie" numero={data.numero} libelle={data.libelle} titre={data.titre} chapeau={data.chapeau} />
      <div className="canaux" style={{ gridColumn: "2", marginTop: 56 }}>
        {data.items.map((c, i) => (
          <Reveal key={c.nom} delay={i * 0.08} className="canal">
            <p className="num" data-testid={`canal-num-${i}`}>{String(i + 1).padStart(2, "0")}</p>
            <h3 className="t-h3" style={{ margin: "14px 0 16px" }}>{c.nom}</h3>
            <p className="t-body" style={{ fontSize: 15, marginTop: 0 }}>{c.texte}</p>
            <Link to={c.url} className="link-arrow" data-testid={`canal-link-${i}`}>Le canal <ArrowRight size={13} strokeWidth={1.5} /></Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function Methode({ data }) {
  if (!data) return null;
  return (
    <section id="methode" className="sec wrap" data-testid="methode-section">
      <SectionHead id="methode" numero={data.numero} libelle={data.libelle} titre={data.titre} chapeau={data.chapeau} />
      <div className="methode" style={{ gridColumn: "2", marginTop: 56 }}>
        {data.steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.07} className="etape">
            <p className="chiffre" style={{ color: i < 2 ? "var(--vert)" : "var(--roi)" }} data-testid={`etape-${s.n}`}>{s.n}</p>
            <p className="titre">{(s.titre || "").split("\n").map((l, k) => (<React.Fragment key={k}>{k > 0 && <br />}{l}</React.Fragment>))}</p>
            <p className="t-body" style={{ fontSize: 14, margin: 0 }}>{s.texte}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function FaqSection({ data }) {
  const [open, setOpen] = useState(0);
  if (!data) return null;
  return (
    <section id="faq" className="sec wrap" data-testid="faq-section">
      <SectionHead id="faq" numero={data.numero} libelle={data.libelle} titre={data.titre} />
      <div style={{ gridColumn: "2", marginTop: 48 }}>
        {data.items.map((it, i) => (
          <div className={`faq-item ${open === i ? "open" : ""}`} key={i}>
            <button className="faq-q" onClick={() => setOpen(open === i ? -1 : i)} aria-expanded={open === i} data-testid={`faq-q-${i}`}>
              <span>{it.q}</span>
              <span className="faq-sign" aria-hidden="true">{open === i ? "–" : "+"}</span>
            </button>
            {open === i && <p className="faq-a t-body" data-testid={`faq-a-${i}`}>{it.a}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

export function useHomeContent() {
  const { data, isLoading } = useContent();
  return { data, isLoading, s: data?.settings || {} };
}
